import React from 'react';
import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import { AnimatedSection } from './AnimatedSection';

/** Label kecil di atas judul: garis + teks uppercase. Dipakai di semua section. */
export const SectionEyebrow = ({ children, className = '' }) => (
  <div className={`inline-flex items-center gap-2.5 ${className}`}>
    <span className="h-px w-8 bg-indigo-500" />
    <span className="text-xs font-semibold uppercase tracking-[0.2em] text-indigo-600 dark:text-indigo-400">
      {children}
    </span>
  </div>
);

/** Header section asimetris: judul di kiri, catatan pendek di kanan. */
export const SectionIntro = ({ eyebrow, title, subtitle, note, testId, className = '' }) => (
  <AnimatedSection className={`grid gap-6 lg:grid-cols-12 lg:items-end ${className}`}>
    <div className="lg:col-span-7" data-testid={testId}>
      {eyebrow && <SectionEyebrow className="mb-5">{eyebrow}</SectionEyebrow>}
      <h2
        className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight text-slate-900 dark:text-white"
        style={{ fontFamily: 'Manrope' }}
      >
        {title}
      </h2>
      {subtitle && (
        <p className="mt-4 text-base md:text-lg text-slate-600 dark:text-zinc-300 max-w-xl">{subtitle}</p>
      )}
    </div>
    {note && (
      <div className="lg:col-span-5 lg:text-right">
        <p className="text-sm text-slate-500 dark:text-zinc-400 lg:ml-auto max-w-sm border-l-2 lg:border-l-0 lg:border-r-2 border-indigo-500/40 pl-4 lg:pl-0 lg:pr-4">
          {note}
        </p>
      </div>
    )}
  </AnimatedSection>
);

/** Daftar poin dengan centang indigo di kotak lembut. */
export const FeatureChecklist = ({ items, className = '', direction = 'left', testId }) => (
  <ul className={`space-y-3 ${className}`} data-testid={testId}>
    {items.filter(Boolean).map((item, idx) => (
      <motion.li
        key={idx}
        className="flex items-start gap-3 text-sm sm:text-base text-slate-700 dark:text-zinc-300"
        initial={{ opacity: 0, x: direction === 'left' ? -16 : 16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.06 * idx, duration: 0.4 }}
      >
        <span className="mt-0.5 grid place-items-center w-5 h-5 shrink-0 rounded-md bg-indigo-50 text-indigo-600 dark:bg-indigo-500/15 dark:text-indigo-300">
          <Check className="w-3.5 h-3.5" />
        </span>
        {item}
      </motion.li>
    ))}
  </ul>
);

/** Bingkai untuk mockup produk supaya semua showcase punya bahasa visual sama. */
export const ShowcaseFrame = ({ children, className = '', testId }) => (
  <motion.div
    data-testid={testId}
    className={`relative rounded-2xl border border-slate-200 bg-white p-4 sm:p-5 transition-[border-color,box-shadow] duration-300 hover:border-indigo-300 shadow-[0_28px_60px_-40px_rgba(15,23,42,0.4)] dark:border-zinc-800 dark:bg-[#0A0A0A] dark:hover:border-indigo-500/50 dark:shadow-[0_28px_60px_-34px_rgba(0,0,0,0.95)] ${className}`}
    whileHover={{ y: -4 }}
    transition={{ duration: 0.25 }}
  >
    <span
      aria-hidden="true"
      className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-inset ring-indigo-500/10"
    />
    <div className="relative">{children}</div>
  </motion.div>
);

/** Cahaya radial lembut sebagai pemberi kedalaman (bukan gradient penuh). */
export const SectionGlow = ({ position = 'left' }) => (
  <div
    aria-hidden="true"
    className={`pointer-events-none absolute h-72 w-72 rounded-full bg-indigo-500/10 blur-3xl dark:bg-indigo-500/20 ${
      position === 'left' ? '-top-24 -left-24' : '-bottom-24 -right-24'
    }`}
  />
);

export default SectionIntro;
